import type { GetStaticProps, NextPage } from "next"
import { paramCase } from "change-case"

import { linkResolver } from "~/api/prismic/linkResolver"
import { getAboutPage } from "~/api/prismic/singles"
import type { AboutPage } from "~/api/prismic/types"
import CustomRichText from "~/components/CustomRichText"
import { FormalLayout, TitledSection } from "~/components/FormalLayout"
import { SEO } from "~/components/SEO"
import { enhanceStaticProps } from "~/utils/next/enhanceStaticProps"
import { getPreviewRef } from "~/utils/prismic/previewData"
import * as RichText from "~/utils/prismic/richText"

type AboutPageProps = { pageContent: AboutPage }

const About: NextPage<AboutPageProps> = ({ pageContent }) => {
  const { body, title } = pageContent.data

  const titleText = RichText.asText(title)
  const sectionTitles = body.map((item) => {
    switch (item.slice_type) {
      case "section": {
        const sectionTitle = RichText.asText(item.primary.section_title)
        const titleId = paramCase(sectionTitle)

        return { title: sectionTitle, id: titleId }
      }

      default:
        return {}
    }
  })

  return (
    <>
      <SEO pageTitle={titleText} path={linkResolver(pageContent)} />
      <FormalLayout title={titleText} sectionTitles={sectionTitles}>
        {body.map((item, index) => {
          switch (item.slice_type) {
            case "section": {
              const { title: sectionTitle, id } = sectionTitles[index]

              return (
                <TitledSection key={sectionTitle} id={id} title={sectionTitle}>
                  <CustomRichText field={item.primary.section_body} />
                </TitledSection>
              )
            }

            default:
              return null
          }
        })}
      </FormalLayout>
    </>
  )
}

export const getStaticProps: GetStaticProps<AboutPageProps> =
  enhanceStaticProps<AboutPageProps>(async ({ previewData }) => {
    const res = await getAboutPage({ ref: getPreviewRef(previewData) })

    if (!res) {
      return { notFound: true }
    }

    return {
      props: { pageContent: res },
    }
  })

export default About
